import { useMemo } from 'react';
import { UserSettings } from 'src/environmentVariables/EnvironmentVariables';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useIsElfaWidgetEligible } from '../../../ElfaWidget/hooks/useIsElfaWidgetEligible';

export interface InterfaceSetting {
	label: string;
	key: keyof UserSettings;
	screenSize: 'mobile' | 'desktop' | 'all';
}

const GENERAL_INTERFACE_SETTINGS: InterfaceSetting[] = [
	{
		label: 'Show P&L on close',
		key: 'showPnlOnClose',
		screenSize: 'all',
	},
	{
		label: 'Show open order confirmation',
		key: 'showTradeConfirmation',
		screenSize: 'all',
	},
	{
		label: 'Show close position confirmation',
		key: 'showClosePositionConfirmation',
		screenSize: 'all',
	},
	{
		label: 'Show fill notifications',
		key: 'showFillNotifications',
		screenSize: 'all',
	},
	{
		label: 'Show favorite markets bar',
		key: 'showMarketBar',
		screenSize: 'desktop',
	},
	{
		label: 'Show Orderbook',
		key: 'showOrderbook',
		screenSize: 'desktop',
	},
	{
		label: 'Blink on value change',
		key: 'blinkOnValueChange',
		screenSize: 'all',
	},
	{
		label: 'Show orderbook on Trade tab',
		key: 'showOrderbookOnMobileTradeView',
		screenSize: 'mobile',
	},
];

const CHART_INTERFACE_SETTINGS: InterfaceSetting[] = [
	{
		label: 'Show positions and orders on chart',
		key: 'showPositionsAndOrdersOnChart',
		screenSize: 'all',
	},
	{
		label: 'Show liquidation price on chart',
		key: 'showLiquidationPriceOnChart',
		screenSize: 'all',
	},
	{
		label: 'Show [B]uys and [S]ells on chart',
		key: 'showBuysAndSellsOnChart',
		screenSize: 'all',
	},
];

const ELFA_WIDGET_SETTING: InterfaceSetting = {
	label: 'Show ELFA Widget',
	key: 'showElfaWidget',
	screenSize: 'all',
};

/**
 * Returns interface settings for the current screen size categorized by "general" and "chart".
 */
export default function useInterfaceSettings() {
	const isMobileScreenSize = useIsMobileScreenSize();
	const shouldShowElfaWidgetSetting = useIsElfaWidgetEligible();
	const screenSize = isMobileScreenSize ? 'mobile' : 'desktop';

	const generalSettings = useMemo(() => {
		const baseSettings = GENERAL_INTERFACE_SETTINGS.filter((setting) =>
			shouldShowForScreenSize(setting, screenSize)
		);

		if (!shouldShowElfaWidgetSetting) {
			return baseSettings;
		}

		return baseSettings.concat(ELFA_WIDGET_SETTING);
	}, [screenSize, shouldShowElfaWidgetSetting]);

	const chartSettings = useMemo(() => {
		return CHART_INTERFACE_SETTINGS.filter((setting) =>
			shouldShowForScreenSize(setting, screenSize)
		);
	}, [screenSize]);

	return { generalSettings, chartSettings };
}

function shouldShowForScreenSize(
	setting: InterfaceSetting,
	screenSize: Exclude<InterfaceSetting['screenSize'], 'all'>
) {
	return setting.screenSize === 'all' || screenSize === setting.screenSize;
}
